<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { computed, reactive, ref, watch } from 'vue'
import { useMutation, useQuery } from '@vue/apollo-composable'
import {
  CreateProductConfiguration,
  CreateSite,
  Mutation,
  PermissionOrganization,
  ProductConfigurationViewArchiveMutation,
  ProductConfigurationViewArchiveMutationVariables,
  ProductConfigurationViewDataQuery,
  ProductConfigurationViewDataQueryVariables,
  ProductConfigurationViewUnarchiveMutation,
  ProductConfigurationViewUnarchiveMutationVariables,
  ProductConfigurationViewUnarchiveProductMutation,
  ProductConfigurationViewUnarchiveProductMutationVariables,
  UpdateProductConfiguration,
  UpdateSite,
} from '@/generated/graphql'
import gql from 'graphql-tag'
import useVuelidate from '@vuelidate/core'
import TextField from '@/components/input/TextField.vue'
import { abbreviation, normalDescription, normalName, required, unique } from '@/validation'
import TextAreaField from '@/components/input/TextAreaField.vue'
import { useRoute } from 'vue-router'
import BopSelector from '@/components/bop/BopSelector.vue'
import Breadcrumb from '@/components/Breadcrumb.vue'
import ImagePicker from '@/components/input/ImagePicker.vue'
import { merge } from 'lodash'
import { SelectOption } from '@/components/input/SelectField.vue'
import { ComponentExposed } from 'vue-component-type-helpers'
import UpsertDialog from '@/components/dialogs/UpsertDialog.vue'
import DeleteDialogNext from '@/components/dialogs/AsyncDeleteDialog.vue'
import AsyncDeleteDialog from '@/components/dialogs/AsyncDeleteDialog.vue'
import AsyncConfirmDialog from '@/components/dialogs/AsyncConfirmDialog.vue'
import ArchiveNotice from '@/components/archive/ArchiveNotice.vue'
import ArchivedChip from '@/components/archive/ArchivedChip.vue'
import useAuthorization from '@/composables/useAuthorization'
import { mockUpdateProductConfiguration } from '@/generated/graphql-mocks'

type CreateModel = Partial<CreateProductConfiguration>
type UpdateModel = Partial<UpdateProductConfiguration>
const modelKeys = Object.keys(mockUpdateProductConfiguration())

const { t } = useI18n()
const { hasOrgAuthz } = useAuthorization()
const route = useRoute()
const productId = computed(() => route.params.productId as string)

type LocalSite = ProductConfigurationViewDataQuery['site']['sites'][0]
type LocalProdConfig =
  ProductConfigurationViewDataQuery['product']['product']['productConfigurations'][0]
type LocalBop = LocalProdConfig['bops'][0]
const fetchQuery = useQuery<
  ProductConfigurationViewDataQuery,
  ProductConfigurationViewDataQueryVariables
>(
  gql`
    query ProductConfigurationViewData($productId: ID!) {
      site {
        sites {
          id
          name
        }
      }
      product {
        product(id: $productId) {
          id
          name
          archived
          productConfigurations {
            id
            name
            abbreviation
            description
            imageUploadId
            archived
            imageUpload {
              id
              imageUrl(options: { width: 500, height: 300 })
            }
            bops {
              id
              site {
                id
                name
              }
              version
              versionStatus
            }
          }
        }
      }
    }
  `,
  () => ({
    productId: productId.value,
  }),
)
const productConfigs = computed<LocalProdConfig[]>(
  () => fetchQuery.result.value?.product.product.productConfigurations || [],
)
const sites = computed<LocalSite[]>(() => fetchQuery.result.value?.site.sites || [])
const product = computed(() => fetchQuery.result.value?.product.product)
const canWrite = computed(
  () => !product.value?.archived && hasOrgAuthz(PermissionOrganization.OrgProductWrite),
)

type Tab = 'active' | 'archived'
const tab = ref<Tab>('active')
const visibleConfigs = computed(() =>
  product.value?.archived
    ? productConfigs.value
    : productConfigs.value.filter((c) => c.archived == (tab.value == 'archived')),
)

const validationRules = {
  name: { required, normalName },
  abbreviation: { required, abbreviation },
  description: { normalDescription },
}

const createModel = reactive<CreateModel>({})
const createTakenAbbreviations = computed(() => productConfigs.value.map((s) => s.abbreviation))
const createValidationRules = merge(validationRules, {
  abbreviation: { unique: unique(createTakenAbbreviations) },
})
const createValidation = useVuelidate<CreateModel>(createValidationRules, createModel)
const createMutation = useMutation<Mutation>(gql`
  mutation ProductConfigurationViewCreate($command: CreateProductConfiguration!) {
    product {
      createProductConfiguration(command: $command) {
        id
      }
    }
  }
`)
const createDialog = ref<ComponentExposed<typeof UpsertDialog<() => Promise<unknown>>>>()
const doCreate = async () => {
  modelKeys.forEach((k) => (createModel[k as keyof CreateModel] = undefined))
  createModel.forProductId = productId.value
  await createDialog.value?.open(() =>
    createMutation.mutate({ command: createModel as CreateSite }).then(() => fetchQuery.refetch()),
  )
}

const updateModel = reactive<UpdateModel>({})
const updateTakenAbbreviations = computed(() =>
  productConfigs.value.filter((pc) => pc.id != updateModel.id).map((s) => s.abbreviation),
)
const updateValidationRules = merge(validationRules, {
  abbreviation: { unique: unique(updateTakenAbbreviations) },
})
const updateValidation = useVuelidate<UpdateModel>(updateValidationRules, updateModel)
const updateMutation = useMutation<Mutation>(gql`
  mutation ProductConfigurationViewUpdate($command: UpdateProductConfiguration!) {
    product {
      updateProductConfiguration(command: $command) {
        id
      }
    }
  }
`)
const updateDialog = ref<ComponentExposed<typeof UpsertDialog<() => Promise<unknown>>>>()
const doUpdate = async (item: LocalProdConfig) => {
  modelKeys.forEach((k) => (updateModel[k as keyof UpdateModel] = item[k as keyof UpdateModel]))
  await updateDialog.value?.open(() =>
    updateMutation.mutate({ command: updateModel as UpdateSite }).then(() => fetchQuery.refetch()),
  )
}

const deleteMutation = useMutation<Mutation>(gql`
  mutation ProductConfigurationViewDelete($id: ID!) {
    product {
      deleteProductConfiguration(id: $id)
    }
  }
`)
const deleteDialog = ref<ComponentExposed<typeof DeleteDialogNext>>()
const doDelete = async (item: LocalProdConfig) => {
  await deleteDialog.value?.open(item.name, () =>
    deleteMutation.mutate({ id: item.id }).then(() => fetchQuery.refetch()),
  )
}

const archiveMutation = useMutation<
  ProductConfigurationViewArchiveMutation,
  ProductConfigurationViewArchiveMutationVariables
>(gql`
  mutation ProductConfigurationViewArchive($id: ID!) {
    product {
      archiveProductConfiguration(id: $id) {
        id
      }
    }
  }
`)
const archiveDialog = ref<ComponentExposed<typeof AsyncConfirmDialog<LocalProdConfig>>>()
const doArchive = async (item: LocalProdConfig) => {
  await archiveDialog.value?.open(
    () => archiveMutation.mutate({ id: item.id }).then(() => fetchQuery.refetch()),
    item,
  )
}

const unarchiveMutation = useMutation<
  ProductConfigurationViewUnarchiveMutation,
  ProductConfigurationViewUnarchiveMutationVariables
>(gql`
  mutation ProductConfigurationViewUnarchive($id: ID!) {
    product {
      unarchiveProductConfiguration(id: $id) {
        id
      }
    }
  }
`)
const unarchiveDialog = ref<ComponentExposed<typeof AsyncConfirmDialog<LocalProdConfig>>>()
const doUnarchive = async (item: LocalProdConfig) => {
  await unarchiveDialog.value?.open(
    () => unarchiveMutation.mutate({ id: item.id }).then(() => fetchQuery.refetch()),
    item,
  )
}

const unarchiveProductMutation = useMutation<
  ProductConfigurationViewUnarchiveProductMutation,
  ProductConfigurationViewUnarchiveProductMutationVariables
>(gql`
  mutation ProductConfigurationViewUnarchiveProduct($id: ID!) {
    product {
      unarchiveProduct(id: $id) {
        id
      }
    }
  }
`)
const unarchiveProductDialog = ref<ComponentExposed<typeof AsyncConfirmDialog<string>>>()
const doUnarchiveProduct = async () => {
  if (!product.value) return

  await unarchiveProductDialog.value?.open(
    () => unarchiveProductMutation.mutate({ id: productId.value }).then(() => fetchQuery.refetch()),
    product.value.name,
  )
}

type BopPerConfigPerSite = {
  [key: string]: {
    [key: string]: {
      site: LocalSite
      bops: LocalBop[]
      selectedBopId: string | undefined
      options: SelectOption<LocalBop>[]
    }
  }
}
const bopsPerConfigPerSite = ref<BopPerConfigPerSite>({})
watch([sites, productConfigs], () => {
  bopsPerConfigPerSite.value = {}
  productConfigs.value.forEach((config) => {
    bopsPerConfigPerSite.value[config.id] = {}
    sites.value.forEach((site) => {
      const bops = config.bops
        .filter((bop) => bop.site.id == site.id)
        .sort((a, b) => b.version - a.version)
      const selectedBopId = bops[0]?.id
      const options = bops.map((b) => ({
        title: `${b.version} (${b.versionStatus})`,
        value: b,
      }))

      bopsPerConfigPerSite.value[config.id][site.id] = { site, bops, selectedBopId, options }
    })
  })
})
</script>

<template>
  <breadcrumb />
  <h1 class="d-flex align-center">
    {{ t('entity.productConfiguration.plural') }}
    <v-spacer />
    <v-btn v-if="canWrite" icon="add" color="primary" @click="doCreate()" />
  </h1>

  <archive-notice v-if="product?.archived" :message="t('component.archive.productArchived')">
    <template v-if="hasOrgAuthz(PermissionOrganization.OrgProductWrite)" #action>
      <v-btn color="primary" variant="flat" prepend-icon="unarchive" @click="doUnarchiveProduct()">
        {{ t('component.archive.unarchiveProduct') }}
      </v-btn>
    </template>
  </archive-notice>

  <v-tabs v-else v-model="tab" class="mb-4">
    <v-tab value="active">{{ t('component.archive.tabActive') }}</v-tab>
    <v-tab value="archived">{{ t('component.archive.tabArchived') }}</v-tab>
  </v-tabs>

  <v-alert v-if="visibleConfigs.length == 0 && !fetchQuery.loading.value" type="info">
    {{
      tab == 'archived' && !product?.archived
        ? t('component.archive.noArchivedItems')
        : t('component.dataTable.noData')
    }}
  </v-alert>
  <v-row>
    <v-col v-for="config in visibleConfigs" :key="config.id" xxl="2" xl="3" lg="3" sm="4" xs="6">
      <v-card :class="{ 'archived-card': config.archived }">
        <v-img
          v-if="config.imageUpload?.imageUrl"
          :src="config.imageUpload.imageUrl"
          height="175px"
        />
        <v-card-title>{{ config.name }}</v-card-title>
        <v-card-text>
          <div v-if="config.archived" class="mb-2"><archived-chip /></div>
          <p>{{ config.description }}</p>

          <h3 class="mt-3">{{ t('entity.bop.plural') }}</h3>
          <bop-selector
            v-for="(site, i) in sites"
            :key="site.id"
            v-model="bopsPerConfigPerSite[config.id][site.id].selectedBopId"
            :product-config-id="config.id"
            :site-id="site.id"
            :label="site.name"
            :bops="bopsPerConfigPerSite[config.id][site.id].bops"
            :is-last="i < sites.length - 1"
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <template v-if="hasOrgAuthz(PermissionOrganization.OrgProductWrite)">
            <v-btn
              v-if="config.archived"
              density="compact"
              size="small"
              icon="unarchive"
              :title="t('button.unarchive')"
              @click="doUnarchive(config)"
            />
            <template v-else>
              <v-btn
                density="compact"
                size="small"
                icon="edit"
                :title="t('button.edit')"
                @click="doUpdate(config)"
              />
              <v-btn
                density="compact"
                size="small"
                icon="archive"
                :title="t('button.archive')"
                @click="doArchive(config)"
              />
            </template>
          </template>
          <v-btn
            v-if="hasOrgAuthz(PermissionOrganization.OrgProductDelete)"
            density="compact"
            size="small"
            icon="delete"
            :title="t('button.delete')"
            @click="doDelete(config)"
          />
        </v-card-actions>
      </v-card>
    </v-col>
  </v-row>

  <async-delete-dialog
    ref="deleteDialog"
    :warning="t('entity.productConfiguration.deleteWarning')"
    hindrance
  />

  <async-confirm-dialog ref="archiveDialog">
    <template #default="{ item }">
      {{
        t('component.archive.archiveConfigurationMessage', {
          name: (item as LocalProdConfig)?.name,
        })
      }}
    </template>
  </async-confirm-dialog>

  <async-confirm-dialog ref="unarchiveDialog">
    <template #default="{ item }">
      <p>
        {{
          t('component.archive.unarchiveConfigurationMessage', {
            name: (item as LocalProdConfig)?.name,
          })
        }}
      </p>
      <p v-if="product?.archived" class="mt-2">
        {{
          t('component.archive.unarchiveConfigurationWithProductMessage', { product: product.name })
        }}
      </p>
    </template>
  </async-confirm-dialog>

  <async-confirm-dialog ref="unarchiveProductDialog">
    <template #default="{ item }">
      {{ t('component.archive.unarchiveProductMessage', { name: item }) }}
    </template>
  </async-confirm-dialog>

  <upsert-dialog
    ref="createDialog"
    type="create"
    :entity="t('entity.productConfiguration.singular')"
    :validation="createValidation"
  >
    <text-field
      v-model="createModel.name"
      :label="t('entity.productConfiguration.field.name')"
      required
      :validation="createValidation.name"
    />
    <text-field
      v-model="createModel.abbreviation"
      :label="t('entity.productConfiguration.field.abbreviation')"
      required
      :validation="createValidation.abbreviation"
    />
    <text-area-field
      v-model="createModel.description"
      :label="t('entity.productConfiguration.field.description')"
      :validation="createValidation.description"
    />
    <image-picker
      v-model="createModel.imageUploadId"
      :label="t('entity.productConfiguration.field.image')"
    />
  </upsert-dialog>

  <upsert-dialog
    ref="updateDialog"
    type="update"
    :entity="t('entity.productConfiguration.singular')"
    :validation="updateValidation"
  >
    <text-field
      v-model="updateModel.name"
      required
      :label="t('entity.productConfiguration.field.name')"
      :validation="updateValidation.name"
    />
    <text-field
      v-model="updateModel.abbreviation"
      required
      :label="t('entity.productConfiguration.field.abbreviation')"
      :validation="updateValidation.abbreviation"
    />
    <text-area-field
      v-model="updateModel.description"
      :label="t('entity.productConfiguration.field.description')"
      :validation="updateValidation.description"
    />
    <image-picker
      v-model="updateModel.imageUploadId"
      :label="t('entity.productConfiguration.field.image')"
    />
  </upsert-dialog>
</template>

<style scoped lang="scss">
.archived-card :deep(.v-img) {
  filter: grayscale(1);
  opacity: 0.6;
}
</style>
